import EmptyListState from "../../bits/EmptyListState/EmptyListState";
import * as React from "react";
import {
    AvailabilityStatus,
    DateFormats,
    IAvailabilitySchedule,
    IOfficeWindow,
    ISchedule,
} from "intake_shared/src/types";
import moment from "../../../../utils/app_moment";
import { v4 as uuidv4 } from "uuid";
import { useLocation } from "react-router-dom";
import { defaultOffices } from "../../../../store/cache/location";
import { CacheContext } from "../../../../contexts/CacheContext";
import { getIntakeEmailSignature } from "../../../../utils/intakeEmailSignature";
import { useToasts } from "react-toast-notifications";
import { ViewAs } from "../../../../constants";
import { IClinician } from "intake_shared/src/types";
import { fetchCliniciansByIds } from "../../../../store/clinician";
import {
    findAvailableSlotsThatMatchOfficeWindow,
    getNowEquivalent,
    getStringFromScheduleDate,
    normalizeOfficeNumber,
    sortStringsWithNumberEndings,
} from "../../../../utils/utils";
import store from "../../../../store";
import styles from "./OfficeWindow.module.scss";
import { cn } from "../../../helpers";
import Loader from "../../bits/Loader/Loader";
import { Dropdown } from "../../bits/Dropdown/Dropdown";
import FilterButton from "../../bits/FilterButton/FilterButton";
import ChevronBackIcon from "../../icons/ChevronBack";
import { Views } from "react-big-calendar";
import { Button } from "../../bits/Button/Button";
import Calendar from "../../calendar/Calendar/Calendar";
import ChevronRightIcon from "../../icons/ChevronRight";
import { Switch } from "../../bits/Switch/Switch";
import CalendarBoxView from "../../calendar/CalendarBoxView/CalendarBoxView";
import { Dialog } from "../../bits/Dialog/Dialog";
import CustomDateSelector from "../../bits/CustomDateSelector/CustomDateSelector";
import { uniq } from "lodash";

interface IOfficeWindowSchedule extends ISchedule {
    clinicianId?: string;
    starting_date?: string;
    office?: string;
}

export default function OfficeWindow() {
    const browserLocation = useLocation();
    const { offices: availableOffices = defaultOffices } = React.useContext(
        CacheContext
    ) || {
        offices: defaultOffices,
    };
    const olc = React.useContext(CacheContext);
    const intakeSignature = getIntakeEmailSignature(olc);
    const { addToast } = useToasts();
    const [showLoader, setShowLoader] = React.useState(false);
    const [selectedLocation, setSelectedLocation] = React.useState(
        (((browserLocation.state || {}) as any).selectedLocation as string) || ""
    );
    const [selectedClinicianId, setSelectedClinicianId] = React.useState(
        ((browserLocation.state || {}) as any).selectedClinicianId || "ALL"
    );
    const [clinicianList, setClinicianList] = React.useState<IClinician[]>([]);
    const [selectedRoom, setSelectedRoom] = React.useState("");
    const [existingSchedules, setExistingSchedules] = React.useState(
        [] as IOfficeWindowSchedule[]
    );
    const [selectedView, setSelectedView] = React.useState(ViewAs.SCHEDULE_VIEW_BOX);
    const [draftOfficeWindow, setDraftOfficeWindow] = React.useState({
        from: new Date(),
        to: moment().add(30, "minutes").toDate(),
    });
    const [showCreateOfficeWindowModal, setShowCreateOfficeWindowModal] =
        React.useState(false);

    const [currentView, setCurrentView] = React.useState(() => {
        const savedView = localStorage.getItem("officeWindow_currentView");
        return savedView || Views.WEEK;
    });
    const [currentDate, setCurrentDate] = React.useState(() => {
        const savedDate = localStorage.getItem("officeWindow_currentDate");
        return savedDate ? new Date(savedDate) : new Date();
    });

    const selectedClinician = clinicianList.find(
        (c) => c.id === selectedClinicianId
    );

    const findAvailableOfficeWindowToDelete = async (
        schedule: IAvailabilitySchedule
    ) => {
        const response = await fetchCliniciansByIds([selectedClinicianId]);
        const location = schedule.location || "";
        const office = schedule.office || "";
        const startTime = schedule.start || "";
        const endTime = schedule.end || "";
        return findAvailableSlotsThatMatchOfficeWindow(
            response[0]?.available,
            location,
            office,
            startTime,
            endTime
        );
    };

    const fetchCliniciansByLocation = React.useCallback(
        async (location: string) => {
            setShowLoader(true);
            setClinicianList(
                (await store.fetchAllCliniciansByLocation(location)) || []
            );
            availableOffices[selectedLocation] &&
                setSelectedRoom(availableOffices[selectedLocation][0]);

            setShowLoader(false);
        },
        [selectedLocation, availableOffices]
    );

    React.useEffect(() => {
        fetchCliniciansByLocation(selectedLocation);
    }, [selectedLocation, fetchCliniciansByLocation]);

    const viewOptions = [
        { value: Views.DAY, label: "Day" },
        { value: Views.WEEK, label: "Week" },
        { value: Views.MONTH, label: "Month" },
    ];

    const goToToday = () => {
        const today = new Date();
        setCurrentDate(today);
        localStorage.setItem("officeWindow_currentDate", today.toISOString());
    };

    const goToPrevious = () => {
        const newDate = new Date(currentDate);
        if (currentView === Views.DAY) {
            newDate.setDate(newDate.getDate() - 1);
        } else if (currentView === Views.WEEK) {
            newDate.setDate(newDate.getDate() - 7);
        } else if (currentView === Views.MONTH) {
            newDate.setMonth(newDate.getMonth() - 1);
        }
        setCurrentDate(newDate);
        localStorage.setItem("officeWindow_currentDate", newDate.toISOString());
    };

    const goToNext = () => {
        const newDate = new Date(currentDate);
        if (currentView === Views.DAY) {
            newDate.setDate(newDate.getDate() + 1);
        } else if (currentView === Views.WEEK) {
            newDate.setDate(newDate.getDate() + 7);
        } else if (currentView === Views.MONTH) {
            newDate.setMonth(newDate.getMonth() + 1);
        }
        setCurrentDate(newDate);
        localStorage.setItem("officeWindow_currentDate", newDate.toISOString());
    };

    const goToView = (newView: string) => {
        setCurrentView(newView as any);
        localStorage.setItem("officeWindow_currentView", newView);
    };

    const getViewLabel = () => {
        switch (currentView) {
            case Views.DAY:
                return "Day";
            case Views.WEEK:
                return "Week";
            case Views.MONTH:
                return "Month";
            default:
                return "Week";
        }
    };

    const getLocationLabel = (location: string) => {
        return location || "Location";
    };

    const getClinicianLabel = (clinicianId: string) => {
        if (clinicianId === "ALL") {
            return "Clinician";
        }

        const selectedClinician = clinicianList.find((c) => c.id === clinicianId);
        if (selectedClinician) {
            return `${selectedClinician.firstname} ${selectedClinician.lastname}`;
        }

        return "Clinician";
    };

    const getOfficeLabel = (office: string) => {
        return office || "Office";
    };

    const formatDateRange = () => {
        const start = moment(currentDate);

        if (currentView === Views.DAY) {
            return start.format("MMM DD, YYYY");
        } else if (currentView === Views.WEEK) {
            const weekStart = start.clone().startOf("week");
            const weekEnd = weekStart.clone().endOf("week");
            return `${weekStart.format("MMM DD")} - ${weekEnd.format(
                "MMM DD, YYYY"
            )}`;
        } else if (currentView === Views.MONTH) {
            return start.format("MMMM YYYY");
        }
        return start.format("MMM DD, YYYY");
    };

    const handleSwitch = (key: ViewAs) => {
        setSelectedView(key);
        localStorage.setItem("view", key);
    };

    const manageOfficeWindow = React.useCallback(
        async (
            action: "add" | "remove" | "update",
            schedule: IOfficeWindowSchedule
        ) => {
            switch (action) {
                case "add": {
                    const bookingData = {
                        id: schedule.id || uuidv4(),
                        clinicianId: selectedClinicianId,
                        office: selectedRoom,
                        location: selectedLocation,
                        start: getStringFromScheduleDate(schedule.start),
                        end: getStringFromScheduleDate(schedule.end),
                    };
                    if (
                        moment(bookingData.end).isBefore(moment(bookingData.start))
                    ) {
                        addToast("End time cannot be before start time", {
                            appearance: "error",
                        });
                        return;
                    }
                    const hasConflictingAvailability = existingSchedules.find(
                        (a) => {
                            const availableWindowStart = getNowEquivalent(
                                moment(getStringFromScheduleDate(a.start))
                            );
                            const scheduleWindowStart = getNowEquivalent(
                                moment(getStringFromScheduleDate(schedule.start))
                            );
                            const availableWindowEnd = getNowEquivalent(
                                moment(getStringFromScheduleDate(a.end))
                            );
                            const scheduleWindowEnd = getNowEquivalent(
                                moment(getStringFromScheduleDate(schedule.end))
                            );
                            const overlappingTime =
                                availableWindowEnd.hour() >
                                    scheduleWindowStart.hour() &&
                                availableWindowStart.hour() <
                                    scheduleWindowEnd.hour();
                            const isSameBooking =
                                availableWindowStart.day() ===
                                    scheduleWindowStart.day() &&
                                overlappingTime &&
                                a.office === selectedRoom &&
                                a.location === selectedLocation;
                            // TODO: Makes sense of this logic.
                            return (
                                isSameBooking ||
                                (overlappingTime &&
                                    a.clinicianId === selectedClinicianId &&
                                    availableWindowStart.day() ===
                                        scheduleWindowStart.day())
                            );
                        }
                    );
                    if (hasConflictingAvailability) {
                        addToast(
                            [
                                `
              Conflicting schedule with ${hasConflictingAvailability.clinicianId},`,
                                `${hasConflictingAvailability.location}, ${hasConflictingAvailability.office}`,
                                `@ ${moment(
                                    getStringFromScheduleDate(
                                        hasConflictingAvailability.start
                                    )
                                ).format(DateFormats.DAY_HOUR_FORMAT)}`,
                            ].join(" "),
                            { appearance: "error", autoDismiss: false }
                        );
                        return;
                    }
                    if (!hasConflictingAvailability) {
                        await store.addClinicianOfficeWindow(bookingData);
                        await fetchCliniciansByLocation(selectedLocation);
                        addToast("Office window saved", {
                            appearance: "success",
                        });
                    }
                    break;
                }
                case "remove": {
                    const selectedClinician = clinicianList.find(
                        (c) => c.id === selectedClinicianId
                    );
                    const updatedOfficeWindows: IOfficeWindow[] =
                        selectedClinician?.office_windows.filter(
                            (w) => w.id !== schedule.id
                        ) || [];
                    const clinicianName = `${selectedClinician?.firstname} ${selectedClinician?.lastname}`;
                    await store.updateClinicianOfficeWindows(
                        selectedClinicianId,
                        updatedOfficeWindows,
                        "office_manager",
                        schedule,
                        clinicianName,
                        "",
                        "",
                        intakeSignature
                    );
                    await fetchCliniciansByLocation(selectedLocation);
                    const response = await fetchCliniciansByIds([
                        selectedClinicianId,
                    ]);
                    const filteredSchedule = response[0]?.available?.filter(
                        (item) => {
                            return findAvailableSlotsThatMatchOfficeWindow(
                                response[0]?.available,
                                schedule.location || "",
                                schedule.office || "",
                                schedule.start || "",
                                schedule.end || ""
                            ).every((scheduleItem) => {
                                return item.id !== scheduleItem.id;
                            });
                        }
                    );
                    await store.updateClinicianAvailability(
                        selectedClinicianId,
                        AvailabilityStatus.AVAILABLE,
                        filteredSchedule
                    );

                    break;
                }
                case "update": {
                    break;
                }
                default: {
                    console.log({ action, schedule });
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            existingSchedules,
            clinicianList,
            selectedClinicianId,
            selectedLocation,
            selectedRoom,
            addToast,
            fetchCliniciansByLocation,
        ]
    );

    React.useEffect(() => {
        const storedData = localStorage.getItem("view");
        if (storedData) {
            setSelectedView(storedData as ViewAs);
        }
    }, []);

    React.useEffect(() => {
        let schedules: IOfficeWindowSchedule[] = [];
        clinicianList.forEach((c) => {
            schedules = schedules.concat(
                c.office_windows
                    .filter((w) => w.location === selectedLocation)
                    .map((w) => {
                        return {
                            clinicianId: c.id,
                            id: w.id,
                            title: `[${w.location} - ${w.office}] ${c.firstname} ${c.lastname}`,
                            start: w.start,
                            end: w.end,
                            category: "time",
                            office: w.office,
                            location: selectedLocation,
                            calendarId: `${
                                availableOffices[selectedLocation].findIndex(
                                    (r) => w.office === r
                                ) + 1
                            }`,
                            action: "admin_deleting_clinician_office_window",
                        };
                    })
            );
        });
        setExistingSchedules(schedules);
    }, [clinicianList, selectedLocation, availableOffices]);

    return (
        <div className={cn(styles.container, "v2-page")}>
            <Dialog
                title={`Create office window for ${selectedClinician?.fullname}`}
                open={showCreateOfficeWindowModal}
                onClose={() => setShowCreateOfficeWindowModal(false)}
                onSave={() => {
                    const schedule: ISchedule = {
                        id: uuidv4(),
                        title: `[${selectedLocation} - ${selectedRoom}] ${selectedClinician?.fullname}`,
                        calendarId: `${
                            availableOffices[selectedLocation].findIndex(
                                (r) => selectedRoom === r
                            ) + 1
                        }`,
                        start: draftOfficeWindow.from.toISOString(),
                        end: draftOfficeWindow.to.toISOString(),
                        category: "time",
                    };
                    manageOfficeWindow("add", schedule);
                    setShowCreateOfficeWindowModal(false);
                }}
                onCancel={() => setShowCreateOfficeWindowModal(false)}
            >
                <form className="v2-form">
                    <CustomDateSelector
                        onChange={(from, to) => {
                            setDraftOfficeWindow({
                                ...draftOfficeWindow,
                                from,
                                to,
                            });
                        }}
                        showEndTime={true}
                    />
                </form>
            </Dialog>
            <Loader show={showLoader} />
            <div className={styles.header}>
                <p className={styles.headerTitle}>Room colors</p>
                <div className={styles.officeColorLegend}>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(255, 87, 51, 0.15)" }}
                    >
                        12060
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(204, 204, 0, 0.15)" }}
                    >
                        328
                    </div>
                    <div
                        style={{ backgroundColor: "rgba(51, 87, 255, 0.15)" }}
                        className={styles.legend}
                    >
                        1
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(243, 51, 255, 0.15)" }}
                    >
                        2
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(255, 51, 161, 0.15)" }}
                    >
                        3
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(125, 38, 207, 0.15)" }}
                    >
                        128
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(255, 140, 51, 0.15)" }}
                    >
                        507
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(51, 255, 140, 0.15)" }}
                    >
                        518
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(140, 51, 255, 0.15)" }}
                    >
                        519
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(255, 99, 71, 0.15)" }}
                    >
                        4
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(51, 255, 87, 0.15)" }}
                    >
                        5
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(0, 153, 153, 0.15)" }}
                    >
                        6
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(243, 134, 48, 0.15)" }}
                    >
                        7
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(102, 51, 153, 0.15)" }}
                    >
                        8
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(0, 191, 255, 0.15)" }}
                    >
                        9
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(255, 105, 180, 0.15)" }}
                    >
                        10
                    </div>
                    <div
                        className={styles.legend}
                        style={{
                            backgroundColor: "rgba(34, 193, 195, 0.15)",
                        }}
                    >
                        11
                    </div>
                    <div
                        className={styles.legend}
                        style={{
                            backgroundColor: "rgba(251, 188, 5, 0.15)",
                        }}
                    >
                        12
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(45, 204, 112, 0.15)" }}
                    >
                        13
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(130, 143, 255, 0.15)" }}
                    >
                        345
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(255, 160, 122, 0.15)" }}
                    >
                        103
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(60, 179, 113, 0.15)" }}
                    >
                        104
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(106, 90, 205, 0.15)" }}
                    >
                        105
                    </div>
                    <div
                        className={styles.legend}
                        style={{ backgroundColor: "rgba(255, 69, 0, 0.15)" }}
                    >
                        206
                    </div>
                </div>
            </div>
            <div className={styles.calendarContent}>
                <div className={styles.calendarHeader}>
                    <div className={styles.calendarHeaderLeft}>
                        <Dropdown>
                            <Dropdown.Trigger>
                                <FilterButton
                                    text={getLocationLabel(selectedLocation)}
                                />
                            </Dropdown.Trigger>
                            <Dropdown.Content width="170px">
                                <Dropdown.Item
                                    key="all"
                                    isSelected={selectedLocation === ""}
                                    onClick={() => {
                                        setSelectedLocation("");
                                    }}
                                >
                                    All
                                </Dropdown.Item>
                                {Object.keys(availableOffices).map((k: string) => (
                                    <Dropdown.Item
                                        key={k}
                                        isSelected={selectedLocation === k}
                                        onClick={() => {
                                            setSelectedLocation(k);
                                            fetchCliniciansByLocation(k);
                                        }}
                                    >
                                        {k}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Content>
                        </Dropdown>
                        <Dropdown>
                            <Dropdown.Trigger>
                                <FilterButton
                                    text={getClinicianLabel(selectedClinicianId)}
                                />
                            </Dropdown.Trigger>
                            <Dropdown.Content width="150px">
                                <Dropdown.Item
                                    key="all"
                                    isSelected={selectedClinicianId === "ALL"}
                                    onClick={() => {
                                        setSelectedClinicianId("ALL");
                                    }}
                                >
                                    All
                                </Dropdown.Item>
                                {clinicianList.map((c) => (
                                    <Dropdown.Item
                                        key={c.id}
                                        isSelected={selectedClinicianId === c.id}
                                        onClick={() => {
                                            setSelectedClinicianId(c.id);
                                        }}
                                    >
                                        {c.firstname} {c.lastname}
                                    </Dropdown.Item>
                                ))}
                            </Dropdown.Content>
                        </Dropdown>
                        {selectedClinicianId !== "ALL" && (
                            <Dropdown>
                                <Dropdown.Trigger>
                                    <FilterButton
                                        text={getOfficeLabel(selectedRoom)}
                                    />
                                </Dropdown.Trigger>
                                <Dropdown.Content width="170px">
                                    {sortStringsWithNumberEndings(
                                        uniq(
                                            availableOffices[selectedLocation] || []
                                        )
                                    ).map((r) => (
                                        <Dropdown.Item
                                            key={r}
                                            isSelected={selectedRoom === r}
                                            onClick={() => {
                                                setSelectedRoom(r);
                                            }}
                                        >
                                            {r}
                                        </Dropdown.Item>
                                    ))}
                                </Dropdown.Content>
                            </Dropdown>
                        )}
                        {selectedClinician && (
                            <Button
                                onClick={() => setShowCreateOfficeWindowModal(true)}
                            >
                                Create office window
                            </Button>
                        )}
                    </div>
                    <div className={styles.calendarHeaderRight}>
                        {selectedView === ViewAs.CALENDAR_VIEW_BOX &&
                            selectedLocation &&
                            availableOffices[selectedLocation].length > 0 && (
                                <div className={styles.calendarToggleBar}>
                                    <Dropdown>
                                        <Dropdown.Trigger>
                                            <FilterButton text={getViewLabel()} />
                                        </Dropdown.Trigger>
                                        <Dropdown.Content width="100px">
                                            {viewOptions.map((option) => (
                                                <Dropdown.Item
                                                    key={option.value}
                                                    onClick={() =>
                                                        goToView(option.value)
                                                    }
                                                >
                                                    {option.label}
                                                </Dropdown.Item>
                                            ))}
                                        </Dropdown.Content>
                                    </Dropdown>

                                    <div className={styles.calendarToggleDate}>
                                        <Button
                                            onClick={goToPrevious}
                                            variant="normal"
                                            className={styles.calendarToggleButton}
                                        >
                                            <ChevronBackIcon />
                                        </Button>

                                        <span className={styles.calendarDate}>
                                            {formatDateRange()}
                                        </span>
                                        <Button
                                            variant="normal"
                                            onClick={goToNext}
                                            className={styles.calendarToggleButton}
                                        >
                                            <ChevronRightIcon />
                                        </Button>
                                    </div>

                                    <Button variant="secondary" onClick={goToToday}>
                                        Today
                                    </Button>
                                </div>
                            )}
                        <Switch
                            switchOptions={[
                                {
                                    title: "List view",
                                    key: ViewAs.SCHEDULE_VIEW_BOX,
                                    onClick: () => {
                                        handleSwitch(ViewAs.SCHEDULE_VIEW_BOX);
                                    },
                                },
                                {
                                    title: "Calendar view",
                                    key: ViewAs.CALENDAR_VIEW_BOX,
                                    onClick: () => {
                                        handleSwitch(ViewAs.CALENDAR_VIEW_BOX);
                                    },
                                },
                            ]}
                            activeSwitch={selectedView}
                        />
                    </div>
                </div>
                <div className={styles.calendarContainer}>
                    {!selectedLocation && existingSchedules.length === 0 && (
                        <EmptyListState
                            cardClass={styles.emptyState}
                            emptyMessage="Choose a location"
                            stateHelperText="Office windows will appear here once you pick a location or clinician above."
                        />
                    )}
                    {selectedView === ViewAs.SCHEDULE_VIEW_BOX &&
                        (selectedLocation || existingSchedules.length > 0) && (
                            <Calendar
                                schedules={existingSchedules.map((s) => {
                                    s.isReadOnly =
                                        s.clinicianId !== selectedClinicianId;
                                    s.isVisible =
                                        s.clinicianId === selectedClinicianId ||
                                        selectedClinicianId === "ALL";
                                    s.calendarId = `office-${normalizeOfficeNumber(
                                        s.office || ""
                                    )}`;
                                    return s;
                                })}
                                onUpdateSchedule={(schedule) => {
                                    manageOfficeWindow("update", schedule);
                                }}
                                onRemoveSchedule={(schedule) => {
                                    manageOfficeWindow("remove", schedule);
                                }}
                                onSelectSchedule={async (schedule) => {
                                    return await findAvailableOfficeWindowToDelete(
                                        schedule
                                    );
                                }}
                                loggedInClinician={selectedClinician}
                            />
                        )}
                    {selectedView === ViewAs.CALENDAR_VIEW_BOX &&
                        selectedLocation &&
                        availableOffices[selectedLocation].length > 0 && (
                            <CalendarBoxView
                                schedules={existingSchedules.map((s) => {
                                    s.isReadOnly =
                                        s.clinicianId !== selectedClinicianId;
                                    s.isVisible =
                                        s.clinicianId === selectedClinicianId ||
                                        selectedClinicianId === "ALL";
                                    return s;
                                })}
                                onUpdateSchedule={(schedule) => {
                                    manageOfficeWindow("update", schedule);
                                }}
                                onRemoveSchedule={(schedule) => {
                                    manageOfficeWindow("remove", schedule);
                                }}
                                onSelectSchedule={async (schedule) => {
                                    return await findAvailableOfficeWindowToDelete(
                                        schedule
                                    );
                                }}
                                loggedInClinician={selectedClinician}
                                currentView={currentView}
                                currentDate={currentDate}
                                onViewChange={setCurrentView}
                                onDateChange={setCurrentDate}
                            />
                        )}
                </div>
            </div>
        </div>
    );
}
